import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Volume2, Pause, Loader2 } from "lucide-react";

/**
 * Play button that streams the pre-generated Portuguese TTS MP3 for a question.
 * Silently renders nothing if the question has no audio yet.
 */
export default function AudioPlayer({ qid, hasAudio }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Reset when question changes
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
    setLoading(false);
  }, [qid]);

  if (!hasAudio) return null;

  const toggle = async () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      return;
    }
    setLoading(true);
    try {
      await audioRef.current.play();
    } catch {
      setLoading(false);
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={`${API}/questions/${qid}/audio`}
        preload="none"
        onPlay={() => { setPlaying(true); setLoading(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onWaiting={() => setLoading(true)}
        onPlaying={() => setLoading(false)}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={toggle}
        data-testid={`audio-play-${qid}`}
        aria-label={playing ? "Pause" : "Play"}
        className="rounded-full h-9 gap-2 border-[hsl(var(--accent))]/40 hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/8"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4 fill-current" /> : <Volume2 className="w-4 h-4" />}
        <span className="text-xs font-mono uppercase tracking-wider">{playing ? "Pausar" : "Ouvir"}</span>
      </Button>
    </>
  );
}
